import { CSSProperties } from 'react';
import { css } from '@emotion/css';
import { styleConstants } from './common';

export const questionCardStyles: {
  card: CSSProperties;
  cardContent: CSSProperties;
  header: (hasResult: boolean) => CSSProperties;
  title: CSSProperties;
  tag: CSSProperties;
  resultContainer: CSSProperties;
  resultLabel: CSSProperties;
  resultText: CSSProperties;
  resultTextEmpty: CSSProperties;
  resultComment: CSSProperties;
  pointsContainer: CSSProperties;
  pointsScrollContainer: CSSProperties;
  pointsLabel: CSSProperties;
  mapPointCard: CSSProperties;
  mapPointCardBody: CSSProperties;
  mapPointSpace: CSSProperties;
  mapPointAvatar: (color: string) => CSSProperties;
  mapPointContent: CSSProperties;
  mapPointContentSpace: CSSProperties;
  mapPointBadge: CSSProperties;
  mapPointCoordinates: CSSProperties;
  mapPointComment: CSSProperties;
  mapPointImagesContainer: CSSProperties;
  mapPointImage: CSSProperties;
  mapPointImageSquare: CSSProperties;
  mapPointImageCounter: CSSProperties;
  photosContainer: CSSProperties;
  photosLabel: CSSProperties;
  photosGrid: CSSProperties;
  photoImage: CSSProperties;
  photoImageGrid: CSSProperties;
  photoImageSquare: CSSProperties;
  photoCounter: CSSProperties;
  photoCounterGrid: CSSProperties;
  photoComment: CSSProperties;
  filesContainer: CSSProperties;
  filesLabel: CSSProperties;
  filesList: CSSProperties;
  fileItem: CSSProperties;
  fileItemHover: CSSProperties;
  fileItemWithHover: string;
  fileIcon: CSSProperties;
  fileContent: CSSProperties;
  fileName: CSSProperties;
  fileSize: CSSProperties;
  fileDownloadIcon: CSSProperties;
  fileComment: CSSProperties;
} = {
  card: {
    borderRadius: styleConstants.sizes.borderRadius,
    border: `1px solid ${styleConstants.colors.border}`,
    boxShadow: '0 1px 4px rgba(82, 196, 26, 0.1)',
    transition: 'all 0.2s ease',
  },

  cardContent: {
    padding: '12px 16px',
  },

  header: (hasResult: boolean) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 4,
    opacity: hasResult ? 1 : 0.6,
  }),

  title: {
    fontSize: 13,
    color: '#262626',
    lineHeight: '1.4',
  },

  tag: {
    marginLeft: styleConstants.sizes.spacing.sm,
    fontSize: 10,
    padding: '2px 6px',
    borderRadius: 4,
    fontWeight: '500',
  },

  resultContainer: {
    marginTop: styleConstants.sizes.spacing.sm,
  },

  resultLabel: {
    fontSize: 12,
  },

  resultText: {
    fontSize: 12,
    color: '#262626',
    display: 'block',
    marginTop: 2,
    lineHeight: '1.4',
  },

  resultTextEmpty: {
    fontSize: 12,
    color: '#bfbfbf',
    display: 'block',
    marginTop: 2,
    fontStyle: 'italic',
  },

  resultComment: {
    fontSize: 11,
    fontStyle: 'italic',
    display: 'block',
    marginTop: 4,
  },

  pointsContainer: {
    marginTop: styleConstants.sizes.spacing.sm,
  },

  pointsScrollContainer: {
    maxHeight: 180,
    overflowY: 'auto',
    marginTop: 4,
    padding: styleConstants.sizes.spacing.sm,
  },

  pointsLabel: {
    fontSize: 12,
    marginBottom: styleConstants.sizes.spacing.sm,
    display: 'block',
  },

  mapPointCard: {
    marginBottom: styleConstants.sizes.spacing.sm,
    borderRadius: styleConstants.sizes.borderRadius,
    border: `1px solid ${styleConstants.colors.border}`,
    transition: 'all 0.2s ease',
  },

  mapPointCardBody: {
    padding: styleConstants.sizes.spacing.md,
  },

  mapPointSpace: {
    width: '100%',
  },

  mapPointAvatar: (color: string) => ({
    backgroundColor: color,
    color: 'white',
    fontSize: 11,
    fontWeight: '600',
    width: 20,
    height: 20,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  }),

  mapPointContent: {
    flex: 1,
    minWidth: 0,
  },

  mapPointContentSpace: {
    width: '100%',
  },

  mapPointBadge: {
    backgroundColor: '#fafafa',
    color: '#8c8c8c',
    border: `1px solid ${styleConstants.colors.border}`,
    fontSize: 9,
    fontWeight: '500',
    minWidth: 32,
    height: 32,
    lineHeight: '30px',
    borderRadius: 4,
  },

  mapPointCoordinates: {
    fontSize: 11,
    color: '#8c8c8c',
    fontFamily: 'monospace',
    display: 'block',
    marginBottom: 4,
  },

  mapPointComment: {
    fontSize: 12,
    color: '#262626',
    lineHeight: '1.4',
    marginBottom: 6,
  },

  mapPointImagesContainer: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
  },

  mapPointImage: {
    width: 32,
    height: 32,
    objectFit: 'cover',
    borderRadius: 4,
    border: `1px solid ${styleConstants.colors.border}`,
  },

  mapPointImageSquare: {
    width: '100%',
    aspectRatio: '1',
    objectFit: 'cover',
    borderRadius: 4,
    border: `1px solid ${styleConstants.colors.border}`,
  },

  mapPointImageCounter: {
    width: 32,
    height: 32,
    backgroundColor: '#fafafa',
    borderRadius: 4,
    border: `1px solid ${styleConstants.colors.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 9,
    color: '#8c8c8c',
    fontWeight: '500',
  },

  photosContainer: {
    marginTop: styleConstants.sizes.spacing.sm,
  },

  photosLabel: {
    fontSize: 12,
    marginBottom: styleConstants.sizes.spacing.sm,
    display: 'block',
  },

  photosGrid: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    marginBottom: styleConstants.sizes.spacing.sm,
  },

  photoImage: {
    width: 48,
    height: 48,
    objectFit: 'cover',
    borderRadius: 6,
    border: `1px solid ${styleConstants.colors.border}`,
  },

  photoImageGrid: {
    width: '100%',
    height: 80,
    objectFit: 'cover',
    borderRadius: styleConstants.sizes.borderRadius,
    border: `1px solid ${styleConstants.colors.border}`,
  },

  photoImageSquare: {
    width: '100%',
    aspectRatio: '1',
    objectFit: 'cover',
    borderRadius: styleConstants.sizes.borderRadius,
    border: `1px solid ${styleConstants.colors.border}`,
  },

  photoCounter: {
    width: 48,
    height: 48,
    backgroundColor: '#fafafa',
    borderRadius: 6,
    border: `1px solid ${styleConstants.colors.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 11,
    color: '#8c8c8c',
    fontWeight: '500',
  },

  photoCounterGrid: {
    width: '100%',
    height: 80,
    backgroundColor: '#fafafa',
    borderRadius: styleConstants.sizes.borderRadius,
    border: `1px solid ${styleConstants.colors.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    color: '#8c8c8c',
    fontWeight: '500',
  },

  photoComment: {
    fontSize: 12,
    color: '#595959',
    fontStyle: 'italic',
    display: 'block',
    marginTop: 4,
  },

  filesContainer: {
    marginTop: styleConstants.sizes.spacing.sm,
  },

  filesLabel: {
    fontSize: 12,
    marginBottom: styleConstants.sizes.spacing.sm,
    display: 'block',
  },

  filesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },

  fileItem: {
    display: 'flex',
    alignItems: 'center',
    gap: styleConstants.sizes.spacing.sm,
    padding: '6px 8px',
    backgroundColor: '#fafafa',
    borderRadius: 4,
    border: `1px solid ${styleConstants.colors.border}`,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },

  fileItemHover: {
    backgroundColor: styleConstants.colors.hover,
    borderColor: '#d9d9d9',
  },

  fileIcon: {
    color: '#8c8c8c',
    fontSize: 12,
  },

  fileContent: {
    flex: 1,
    minWidth: 0,
  },

  fileName: {
    fontSize: 12,
    color: '#262626',
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  fileSize: {
    fontSize: 10,
    color: '#8c8c8c',
  },

  fileDownloadIcon: {
    color: '#8c8c8c',
    fontSize: 11,
  },

  fileComment: {
    fontSize: 12,
    color: '#595959',
    fontStyle: 'italic',
    display: 'block',
    marginTop: styleConstants.sizes.spacing.sm,
  },

  fileItemWithHover: css`
    display: flex;
    align-items: center;
    gap: ${styleConstants.sizes.spacing.sm}px;
    padding: 6px 8px;
    background-color: #fafafa;
    border-radius: 4px;
    border: 1px solid ${styleConstants.colors.border};
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
      background-color: ${styleConstants.colors.hover};
      border-color: #d9d9d9;
    }
  `,
};

export const questionTypeConfig = {
  labels: {
    map: 'Map',
    text: 'Text',
    number: 'Number',
    select: 'Select',
    photos: 'Photos',
    files: 'Files',
  },
  colors: {
    map: 'blue',
    text: 'green',
    number: 'orange',
    select: 'purple',
    photos: 'magenta',
    files: 'cyan',
  },
} as const;
